/**
 * Prazo em valor + unidade (dias/semanas/meses).
 *
 * O médico pensa "volta em 3 semanas", não "volta em 21 dias". A persistência
 * continua em dias (canônico), então nada de dado antigo precisa migrar — a
 * conversão vive no modelo de desfecho.
 */
import React from 'react';
import { Box, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  DURATION_UNIT_LABEL,
  type Duration,
  type DurationUnit,
} from './evolutionOutcomeModel';
import { EVOLUTION_THEME } from './evolutionTheme';

const UNITS: DurationUnit[] = ['days', 'weeks', 'months'];

/**
 * Atalhos para os prazos mais comuns. "1 ano" é mais legível que "12 meses",
 * mas guardamos em meses para não inventar uma quarta unidade.
 */
const PRESETS: Array<{ label: string; duration: Duration }> = [
  { label: '7 dias', duration: { value: 7, unit: 'days' } },
  { label: '15 dias', duration: { value: 15, unit: 'days' } },
  { label: '1 mês', duration: { value: 1, unit: 'months' } },
  { label: '3 meses', duration: { value: 3, unit: 'months' } },
  { label: '6 meses', duration: { value: 6, unit: 'months' } },
  { label: '1 ano', duration: { value: 12, unit: 'months' } },
];

interface DurationFieldProps {
  label: string;
  value: Duration | null;
  onChange: (next: Duration | null) => void;
  /** Destaca a borda quando o campo é obrigatório e está vazio. */
  required?: boolean;
  showError?: boolean;
  helperText?: string;
  disabled?: boolean;
}

const sameDuration = (a: Duration | null, b: Duration) =>
  Boolean(a && a.value === b.value && a.unit === b.unit);

const DurationField: React.FC<DurationFieldProps> = ({
  label,
  value,
  onChange,
  required = false,
  showError = false,
  helperText,
  disabled = false,
}) => {
  const isEmpty = !value || !Number(value.value);
  const hasError = showError && required && isEmpty;

  const setValue = (raw: string) => {
    const parsed = Number(raw);
    if (!raw.trim() || !Number.isFinite(parsed) || parsed <= 0) {
      onChange(null);
      return;
    }
    onChange({ value: Math.round(parsed), unit: value?.unit || 'days' });
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 700,
          color: hasError ? EVOLUTION_THEME.cancelledText : EVOLUTION_THEME.navy,
          mb: 0.75,
        }}
      >
        {label}
        {required && ' *'}
      </Typography>

      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
        {PRESETS.map((preset) => {
          const selected = sameDuration(value, preset.duration);
          return (
            <Chip
              key={preset.label}
              label={preset.label}
              size="small"
              disabled={disabled}
              onClick={() => onChange(preset.duration)}
              variant={selected ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 600,
                ...(selected
                  ? { bgcolor: EVOLUTION_THEME.teal, color: '#fff', '&:hover': { bgcolor: EVOLUTION_THEME.tealDark } }
                  : { borderColor: EVOLUTION_THEME.border, color: EVOLUTION_THEME.tealDark }),
              }}
            />
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <TextField
          size="small"
          type="number"
          label="Prazo"
          disabled={disabled}
          value={value?.value ?? ''}
          onChange={(e) => setValue(e.target.value)}
          error={hasError}
          inputProps={{ min: 1 }}
          sx={{ width: 110 }}
        />
        <TextField
          size="small"
          select
          label="Unidade"
          disabled={disabled}
          value={value?.unit || 'days'}
          onChange={(e) =>
            onChange({ value: value?.value || 1, unit: e.target.value as DurationUnit })
          }
          sx={{ width: 140 }}
        >
          {UNITS.map((unit) => (
            <MenuItem key={unit} value={unit}>
              {DURATION_UNIT_LABEL[unit]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {(helperText || hasError) && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.6,
            color: hasError ? EVOLUTION_THEME.cancelledText : EVOLUTION_THEME.slate,
          }}
        >
          {hasError ? 'Obrigatório para o CRM saber quando agir.' : helperText}
        </Typography>
      )}
    </Box>
  );
};

export default DurationField;
